import React, { useState } from 'react'
import PropTypes from 'prop-types'
import {
	Clock,
	CheckCircle,
	XCircle,
	PlayCircle,
	AlertCircle,
	Calendar,
	User,
	FileText,
	FolderOpen,
	Shield,
	Tag,
	ExternalLink,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { Button } from '@components/common'
import { useAuth } from '@context/AuthContext'
import taskService from '@services/taskService'
import './WorkflowOverviewTab.scss'

/**
 * WorkflowOverviewTab Component
 *
 * Displays workflow progress, timeline visualization, and workflow steps
 * Includes action buttons for task state transitions (start, submit, approve, reject)
 *
 * IMPORTANT: At the first workflow step, evidence document is MANDATORY.
 * Users cannot submit without uploading at least one evidence document.
 */
const WorkflowOverviewTab = ({
	task,
	workflowInfo,
	onTaskAction,
	actionLoading = false,
	documentsCount = 0,
}) => {
	const { user } = useAuth()
	const [showRejectModal, setShowRejectModal] = useState(false)
	const [rejectRemarks, setRejectRemarks] = useState('')

	// Permission checks using updated taskService helpers
	// Flow: not_started → Start → in_progress → Submit → pending_review → Accept → in_progress → Submit/Complete → approved
	const canStart = taskService.canStart(task, user)
	const canSubmit = taskService.canSubmit(task, user)
	const canAccept = taskService.canAccept(task, user?.role_slug)
	const canReject = taskService.canReject(task, user?.role_slug)
	const canComplete = taskService.canComplete(task, user)
	const isLastStep = taskService.isLastWorkflowStep(task)
	const isLocked = taskService.isLocked(task)

	// Evidence validation for first workflow step
	const isFirstStep = taskService.isFirstWorkflowStep(task)
	const evidenceRequired = isFirstStep && documentsCount === 0
	const submitCheck = taskService.canSubmitWithEvidence(task, user, documentsCount)
	const completeCheck = taskService.canCompleteWithEvidence(task, user, documentsCount)

	// Helper functions
	const getProgressPercentage = () => {
		if (!workflowInfo || !workflowInfo.total_steps) return 0
		if (task?.workflow_status === 'approved') return 100

		// Progress is based on completed steps, not current step
		// At step 1 of 3: progress = 0/3 = 0%
		// At step 2 of 3: progress = 1/3 = 33%
		// At step 3 of 3: progress = 2/3 = 66%
		// Only when approved: progress = 100%
		const currentIndex = workflowInfo.current_step_index ?? 0
		return Math.round((currentIndex / workflowInfo.total_steps) * 100)
	}

	const getStepStatus = (stepIndex) => {
		if (!workflowInfo) return 'pending'

		const currentIndex = workflowInfo.current_step_index ?? 0

		if (task?.workflow_status === 'approved') return 'completed'
		if (stepIndex < currentIndex) return 'completed'
		if (stepIndex === currentIndex) return 'in-progress'
		return 'pending'
	}

	const getStepIcon = (status) => {
		switch (status) {
			case 'completed':
				return '✓'
			case 'in-progress':
				return '⟳'
			case 'pending':
				return '○'
			default:
				return '○'
		}
	}

	const formatDate = (dateString) => {
		if (!dateString) return 'N/A'
		const date = new Date(dateString)
		return date.toLocaleString('en-US', {
			month: 'short',
			day: 'numeric',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		})
	}

	const formatDateShort = (dateString) => {
		if (!dateString) return 'N/A'
		const date = new Date(dateString)
		return date.toLocaleDateString('en-US', {
			month: 'short',
			day: 'numeric',
			year: 'numeric',
		})
	}

	const getDueDateStatus = (dueDate) => {
		if (!dueDate) return null
		const date = new Date(dueDate)
		const today = new Date()
		const diffDays = Math.ceil((date - today) / (1000 * 60 * 60 * 24))

		if (diffDays < 0) {
			return { text: `${Math.abs(diffDays)} days overdue`, isOverdue: true }
		} else if (diffDays === 0) {
			return { text: 'Due today', isOverdue: false, isToday: true }
		} else if (diffDays <= 3) {
			return { text: `Due in ${diffDays} days`, isOverdue: false, isUrgent: true }
		} else {
			return { text: `Due in ${diffDays} days`, isOverdue: false }
		}
	}

	// Action handlers
	const handleStartTask = () => {
		if (!window.confirm('Are you sure you want to start this task?')) return
		onTaskAction?.('start')
	}

	const handleSubmitForReview = () => {
		// Check evidence requirement at first step
		if (!submitCheck.canSubmit) {
			alert(submitCheck.reason || 'Cannot submit task')
			return
		}
		if (!window.confirm('Are you sure you want to submit this task for review?')) return
		onTaskAction?.('submit')
	}

	const handleAcceptTask = () => {
		if (!window.confirm('Are you sure you want to accept this task?')) return
		onTaskAction?.('accept')
	}

	const handleCompleteTask = () => {
		// Check evidence requirement for single-step workflows
		if (!completeCheck.canComplete) {
			alert(completeCheck.reason || 'Cannot complete task')
			return
		}
		if (!window.confirm('Are you sure you want to complete this task? This will finalize the workflow.')) return
		onTaskAction?.('complete')
	}

	const handleApproveTask = () => {
		if (!window.confirm('Are you sure you want to approve this task?')) return
		onTaskAction?.('approve')
	}

	const handleRejectTask = () => {
		if (!rejectRemarks.trim()) {
			alert('Please provide rejection remarks')
			return
		}
		onTaskAction?.('reject', { remarks: rejectRemarks })
		setShowRejectModal(false)
		setRejectRemarks('')
	}

	const progressPercentage = getProgressPercentage()
	const workflowSteps = workflowInfo?.steps || []
	const dueDateStatus = getDueDateStatus(task?.due_date)

	if (!task) {
		return (
			<div className="workflow-overview-tab workflow-overview-tab--empty">
				<AlertCircle size={48} />
				<p>No task data available</p>
			</div>
		)
	}

	return (
		<div className="workflow-overview-tab">
			{/* Task Info Card */}
			<div className="task-info-card">
				<div className="task-info-card__header">
					<div className="task-info-card__title-section">
						<h2 className="task-info-card__title">{task.title}</h2>
						{task.control_reference && (
							<span className="task-info-card__control-ref">
								<Shield size={14} />
								{task.control_reference}
							</span>
						)}
					</div>
					<div className="task-info-card__badges">
						<span className={`status-badge status-badge--${task.workflow_status}`}>
							{taskService.getStatusText(task.workflow_status)}
						</span>
						{isLocked && (
							<span className="status-badge status-badge--locked">
								🔒 Locked
							</span>
						)}
						{task.approval_status && task.approval_status !== 'pending' && (
							<span className={`status-badge status-badge--approval-${task.approval_status}`}>
								{task.approval_status === 'accepted' ? '✓ Accepted' :
								 task.approval_status === 'excused' ? '⚠ Excused' : task.approval_status}
							</span>
						)}
					</div>
				</div>

				{task.description && (
					<p className="task-info-card__description">{task.description}</p>
				)}

				{/* Task Meta - Full Width Items (Project & Workflow) */}
				<div className="task-info-card__meta-full">
					{task.project_name && (
						<div className="meta-row">
							<span className="meta-row__label">
								<FolderOpen size={14} />
								Project
							</span>
							<span className="meta-row__value">
								<Link to={`/projects/${task.project_id}`} className="meta-link">
									{task.project_name}
									<ExternalLink size={12} />
								</Link>
							</span>
						</div>
					)}

					{task.workflow_name && (
						<div className="meta-row">
							<span className="meta-row__label">
								<FileText size={14} />
								Workflow
							</span>
							<span className="meta-row__value">{task.workflow_name}</span>
						</div>
					)}
				</div>

				{/* Task Meta - Grid Items (2x2 layout) */}
				<div className="task-info-card__meta-grid">
					{task.certificate_name && (
						<div className="meta-item">
							<span className="meta-item__label">
								<Shield size={14} />
								Certificate/Standard
							</span>
							<span className="meta-item__value">{task.certificate_name}</span>
						</div>
					)}

					{task.assigned_to_name && (
						<div className="meta-item">
							<span className="meta-item__label">
								<User size={14} />
								Assigned To
							</span>
							<span className="meta-item__value">{task.assigned_to_name}</span>
						</div>
					)}

					{task.due_date && (
						<div className="meta-item">
							<span className="meta-item__label">
								<Calendar size={14} />
								Due Date
							</span>
							<span className={`meta-item__value ${dueDateStatus?.isOverdue ? 'meta-item__value--overdue' : ''} ${dueDateStatus?.isUrgent ? 'meta-item__value--urgent' : ''}`}>
								{formatDateShort(task.due_date)}
								{dueDateStatus && (
									<span className="meta-item__due-status">({dueDateStatus.text})</span>
								)}
							</span>
						</div>
					)}

					{task.weightage && (
						<div className="meta-item">
							<span className="meta-item__label">
								<Tag size={14} />
								Weightage
							</span>
							<span className="meta-item__value">{task.weightage}%</span>
						</div>
					)}
				</div>

				{/* Action Buttons - Dynamic based on workflow state */}
				{/*
				  Workflow Flow:
				  1. not_started/rejected → Start Task → in_progress
				  2. in_progress → Submit for Review → pending_review
				  3. pending_review → Accept → in_progress (approver working)
				  4. in_progress (not last step) → Submit for Review → pending_review
				  5. in_progress (last step) → Confirm as Completed → approved (100%)

				  Reject can happen from pending_review or in_progress (after accepting)
				*/}
				{!isLocked && (canStart || canSubmit || canAccept || canReject || canComplete) && (
					<div className="task-info-card__actions">
						{/* Start Task Button - for not_started, pending_assignment, rejected tasks */}
						{canStart && (
							<Button
								variant="primary"
								onClick={handleStartTask}
								disabled={actionLoading}
							>
								<PlayCircle size={18} />
								{actionLoading ? 'Starting...' : 'Start Task'}
							</Button>
						)}

						{/* Accept Button - for pending_review tasks (approver accepts to start working) */}
						{canAccept && (
							<Button
								variant="success"
								onClick={handleAcceptTask}
								disabled={actionLoading}
							>
								<CheckCircle size={18} />
								{actionLoading ? 'Accepting...' : 'Accept Task'}
							</Button>
						)}

						{/* Reject Button - for pending_review or in_progress tasks (approver rejects) */}
						{canReject && (
							<Button
								variant="danger"
								onClick={() => setShowRejectModal(true)}
								disabled={actionLoading}
							>
								<XCircle size={18} />
								Reject
							</Button>
						)}

						{/* Submit for Review Button - for in_progress tasks (not last step) */}
						{canSubmit && !isLastStep && (
							<div className="action-button-wrapper">
								<Button
									variant="primary"
									onClick={handleSubmitForReview}
									disabled={actionLoading || !submitCheck.canSubmit}
									title={!submitCheck.canSubmit ? submitCheck.reason : ''}
								>
									<CheckCircle size={18} />
									{actionLoading ? 'Submitting...' : 'Submit for Review'}
								</Button>
								{evidenceRequired && (
									<span className="evidence-warning">
										<AlertCircle size={14} />
										Upload evidence document to submit
									</span>
								)}
							</div>
						)}

						{/* Complete Task Button - for in_progress tasks at last step */}
						{canComplete && (
							<div className="action-button-wrapper">
								<Button
									variant="success"
									onClick={handleCompleteTask}
									disabled={actionLoading || !completeCheck.canComplete}
									title={!completeCheck.canComplete ? completeCheck.reason : ''}
								>
									<CheckCircle size={18} />
									{actionLoading ? 'Completing...' : 'Confirm as Completed'}
								</Button>
								{evidenceRequired && (
									<span className="evidence-warning">
										<AlertCircle size={14} />
										Upload evidence document to complete
									</span>
								)}
							</div>
						)}
					</div>
				)}

				{isLocked && (
					<div className="task-info-card__locked-notice">
						<AlertCircle size={18} />
						<span>This task is locked and cannot be modified.</span>
					</div>
				)}
			</div>

			{/* Progress Bar Section */}
			{workflowInfo && (
				<div className="progress-section">
					<div className="progress-header">
						<h3 className="progress-header__title">Workflow Progress</h3>
						<span className="progress-header__percentage">{progressPercentage}%</span>
					</div>
					<div className="progress-bar">
						<div
							className="progress-bar__fill"
							style={{ width: `${progressPercentage}%` }}
						></div>
					</div>
					<div className="progress-info">
						<span>
							Step {(workflowInfo?.current_step_index ?? 0) + 1} of{' '}
							{workflowInfo?.total_steps || 0}
						</span>
						{workflowInfo?.current_step && (
							<span className="current-step-name">
								Current: {workflowInfo.current_step.role_name || 'N/A'}
							</span>
						)}
					</div>
				</div>
			)}

			{/* Timestamps Card */}
			<div className="timestamps-card">
				<h3 className="timestamps-card__title">
					<Clock size={18} />
					Timeline
				</h3>
				<div className="timestamps-card__grid">
					<div className="timestamp-item">
						<span className="timestamp-item__label">Created</span>
						<span className="timestamp-item__value">{formatDate(task.created_at)}</span>
					</div>
					{task.started_at && (
						<div className="timestamp-item">
							<span className="timestamp-item__label">Started</span>
							<span className="timestamp-item__value">{formatDate(task.started_at)}</span>
						</div>
					)}
					{task.completed_at && (
						<div className="timestamp-item">
							<span className="timestamp-item__label">Completed</span>
							<span className="timestamp-item__value">{formatDate(task.completed_at)}</span>
						</div>
					)}
					{task.updated_at && (
						<div className="timestamp-item">
							<span className="timestamp-item__label">Last Updated</span>
							<span className="timestamp-item__value">{formatDate(task.updated_at)}</span>
						</div>
					)}
				</div>
			</div>

			{/* Workflow Timeline */}
			{workflowSteps.length > 0 && (
				<div className="timeline-section">
					<h3 className="section-title">Workflow Steps</h3>

					<div className="timeline">
						{workflowSteps.map((step, index) => {
							const status = getStepStatus(index)
							const isActive = index === workflowInfo?.current_step_index

							return (
								<div
									key={step.id || index}
									className={`timeline-item timeline-item--${status} ${
										isActive ? 'timeline-item--active' : ''
									}`}
								>
									{/* Timeline connector line */}
									{index < workflowSteps.length - 1 && (
										<div
											className={`timeline-connector ${
												status === 'completed' ? 'timeline-connector--completed' : ''
											}`}
										></div>
									)}

									{/* Step icon */}
									<div className={`timeline-icon timeline-icon--${status}`}>
										<span className="timeline-icon__symbol">{getStepIcon(status)}</span>
									</div>

									{/* Step content */}
									<div className="timeline-content">
										<div className="timeline-content__header">
											<h4 className="timeline-content__title">
												Step {step.step_number || index + 1}: {step.role_name || step.name || 'Unknown'}
											</h4>
											<span className={`step-status step-status--${status}`}>
												{status === 'completed'
													? 'Completed'
													: status === 'in-progress'
													? 'In Progress'
													: 'Pending'}
											</span>
										</div>

										{step.description && (
											<p className="timeline-content__description">{step.description}</p>
										)}

										<div className="timeline-content__details">
											{step.sla_hours && (
												<div className="detail-item">
													<span className="detail-item__label">SLA:</span>
													<span className="detail-item__value">{step.sla_hours} hours</span>
												</div>
											)}
											{step.evidence_required && (
												<div className="detail-item">
													<span className="detail-item__label">Evidence:</span>
													<span className="detail-item__value">Required</span>
												</div>
											)}
											{step.assigned_to_name && (
												<div className="detail-item">
													<span className="detail-item__label">Assigned to:</span>
													<span className="detail-item__value">{step.assigned_to_name}</span>
												</div>
											)}
										</div>

										{/* Timestamp for completed steps */}
										{status === 'completed' && step.completed_at && (
											<div className="timeline-timestamp">
												<CheckCircle size={14} />
												<span>Completed on {formatDate(step.completed_at)}</span>
											</div>
										)}

										{/* Active indicator */}
										{isActive && (
											<div className="timeline-active-indicator">
												<PlayCircle size={14} />
												<span>Current Step</span>
											</div>
										)}
									</div>
								</div>
							)
						})}
					</div>
				</div>
			)}

			{/* Reject Modal */}
			{showRejectModal && (
				<div className="modal-overlay" onClick={() => setShowRejectModal(false)}>
					<div className="modal-content" onClick={(e) => e.stopPropagation()}>
						<h3 className="modal-title">Reject Task</h3>
						<p className="modal-message">
							Please provide remarks explaining why this task is being rejected.
							The task will be returned to the previous assignee.
						</p>
						<textarea
							className="modal-textarea"
							placeholder="Enter rejection remarks (required)..."
							value={rejectRemarks}
							onChange={(e) => setRejectRemarks(e.target.value)}
							rows={4}
						/>
						<div className="modal-actions">
							<Button
								variant="outline"
								onClick={() => {
									setShowRejectModal(false)
									setRejectRemarks('')
								}}
								disabled={actionLoading}
							>
								Cancel
							</Button>
							<Button
								variant="danger"
								onClick={handleRejectTask}
								disabled={actionLoading || !rejectRemarks.trim()}
							>
								{actionLoading ? 'Rejecting...' : 'Reject Task'}
							</Button>
						</div>
					</div>
				</div>
			)}
		</div>
	)
}

WorkflowOverviewTab.propTypes = {
	task: PropTypes.object,
	workflowInfo: PropTypes.object,
	onTaskAction: PropTypes.func,
	actionLoading: PropTypes.bool,
	documentsCount: PropTypes.number,
}

export default WorkflowOverviewTab
